import { useEffect, useRef } from 'react';
import { useUIStore } from '../store/uiStore';
import { isEscapeLayerOpen } from '../utils/escapeStack';
import { useDesktopShell } from './useDesktopShell';
import { shouldHandleShortcut } from './shortcutGuard';
import type { GlobalShortcutActions } from './useGlobalShortcuts';

export type { GlobalShortcutActions };

/**
 * One capture-phase document keydown listener for every global shortcut:
 * ⌘K/Ctrl+K (palette), "/" (search, map route only), L/K/P/N (single-letter
 * tools), and "?" (shortcuts help). Desktop shell only — mirrors
 * useDesktopShell's own reasoning (native and narrow web never get this
 * chrome). Capture phase because react-native-web's TextInput calls
 * stopPropagation() on every keydown (see utils/escapeStack's comment) —
 * without capture, a focused field anywhere else on the page (a sheet's
 * text input, the plan panel's title field) would silently eat every key
 * these shortcuts need.
 *
 * Checked, in order, before grep turned up nothing that collides: existing
 * keydown handlers in AddressSearchBar ("/"), useDefensePlanDraw.web.ts and
 * DefensePlanPanel.web.tsx (Escape/Enter/Space), usePlanMapPlacementLayer.web
 * (Space), and useSlideshowKeys.ts (Escape/arrows/space). None claims L, K,
 * P, N, or "?" — "/" already has a handler in AddressSearchBar, but it does
 * the same thing (expand + focus the bar), so this duplicates rather than
 * conflicts with it.
 */
export function useGlobalShortcuts(actions: GlobalShortcutActions): void {
  const isDesktopShell = useDesktopShell();
  const presentationActive = useUIStore((s) => s.presentationActive);
  const planDrawTool = useUIStore((s) => s.planDrawTool);

  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const guardStateRef = useRef({ isPresenting: presentationActive, isDrawingPlan: false });
  guardStateRef.current = {
    isPresenting: presentationActive,
    isDrawingPlan: planDrawTool != null,
  };

  useEffect(() => {
    if (!isDesktopShell) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const isMetaCombo = (e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k';
      if (isMetaCombo) {
        e.preventDefault();
        actionsRef.current.onTogglePalette();
        return;
      }

      if (
        !shouldHandleShortcut(e, {
          ...guardStateRef.current,
          isDialogOpen: isEscapeLayerOpen(),
        })
      ) {
        return;
      }

      if (e.key === '?') {
        e.preventDefault();
        actionsRef.current.onOpenHelp();
        return;
      }
      if (e.key === '/') {
        if (!actionsRef.current.isOnMapRoute) return;
        e.preventDefault();
        actionsRef.current.onFocusSearch();
        return;
      }
      switch (e.key.toLowerCase()) {
        case 'l':
          e.preventDefault();
          actionsRef.current.onToggleLayers();
          break;
        case 'k':
          e.preventDefault();
          actionsRef.current.onToggleKey();
          break;
        case 'p':
          e.preventDefault();
          actionsRef.current.onTogglePresent();
          break;
        case 'n':
          e.preventDefault();
          actionsRef.current.onNewObservation();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isDesktopShell]);
}
